import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import type { KnownFeatureFlagKey } from '@/lib/featureFlagsCatalog';
import { getProtectedFunctionHeaders } from '@/lib/protectedFunctionAuth';
import type { FeatureReleaseStage } from '@/components/ui/feature-stage-badge';

export type FeatureFlagTargetingMode = 'all_users' | 'specific_users' | 'specific_marketplace_listings';

export type ResolvedFeatureFlag = {
  key: string;
  enabled: boolean;
  releaseStage?: FeatureReleaseStage;
  targetingMode: FeatureFlagTargetingMode;
  reason: 'disabled' | 'enabled_all_users' | 'enabled_for_user' | 'not_targeted';
};

type FeatureFlagsResolveResponse = {
  flags: ResolvedFeatureFlag[];
};

interface UseFeatureFlagOptions {
  userId?: string;
  defaultEnabled?: boolean;
}

function normalizeUserId(userId?: string) {
  const normalized = userId?.trim();
  return normalized ? normalized : null;
}

async function fetchFeatureFlags(keys: KnownFeatureFlagKey[], userId?: string) {
  const headers = await getProtectedFunctionHeaders('feature-flags-resolve', {
    expectedUserId: userId,
  });
  const { data, error } = await supabase.functions.invoke('feature-flags-resolve', {
    body: { keys },
    headers,
  });

  if (error) {
    throw error;
  }

  return ((data as FeatureFlagsResolveResponse | null)?.flags ?? []).reduce<Record<string, ResolvedFeatureFlag>>(
    (accumulator, flag) => {
      accumulator[flag.key] = flag;
      return accumulator;
    },
    {},
  );
}

export function useFeatureFlags(keys: KnownFeatureFlagKey[], options: UseFeatureFlagOptions = {}) {
  const stableKeys = useMemo(
    () => Array.from(new Set(keys.map((key) => key.trim()).filter(Boolean))).sort(),
    [keys],
  );
  const explicitUserId = normalizeUserId(options.userId);
  const [resolvedUserId, setResolvedUserId] = useState<string | null>(explicitUserId);

  useEffect(() => {
    let active = true;

    if (explicitUserId) {
      setResolvedUserId(explicitUserId);
      return () => {
        active = false;
      };
    }

    setResolvedUserId(null);

    void supabase.auth.getSession().then(({ data }) => {
      if (!active) {
        return;
      }

      setResolvedUserId(normalizeUserId(data.session?.user?.id));
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) {
        return;
      }

      setResolvedUserId(normalizeUserId(session?.user?.id));
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [explicitUserId]);
  return useQuery({
    queryKey: ['feature-flags', resolvedUserId ?? 'anonymous', stableKeys],
    queryFn: () => fetchFeatureFlags(stableKeys, resolvedUserId ?? undefined),
    enabled: stableKeys.length > 0 && Boolean(resolvedUserId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useFeatureFlag(key: KnownFeatureFlagKey, options: UseFeatureFlagOptions = {}) {
  const query = useFeatureFlags([key], options);
  const flag = key ? query.data?.[key] : undefined;

  return {
    ...query,
    flag,
    enabled: flag?.enabled ?? options.defaultEnabled ?? false,
  };
}
